import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

class FreeShippingBar extends HTMLElement {
  constructor() {
    super();
    /** @type {{id: number, node: Element, target: number, label: string}[]} */
    this.activeTiers = [];
    this.originalFetch = null;
  }

  connectedCallback() {
    // 1. Get tier thresholds from component attributes instead of data attributes
    const t1 = parseFloat(this.getAttribute('tier1') || '') || 0;
    const t2 = parseFloat(this.getAttribute('tier2') || '') || 0;
    const t3 = parseFloat(this.getAttribute('tier3') || '') || 0;

    // 2. Select DOM nodes locally scoped inside this element
    this.barFill = this.querySelector('#shipping-bar-fill');
    this.textMsg = this.querySelector('#shipping-bar-text');
    const node1 = this.querySelector('#node-1');
    const node2 = this.querySelector('#node-2');
    const node3 = this.querySelector('#node-3');

    // 3. Build active tier configurations
    this.activeTiers = [];
    if (t1 > 0 && node1) this.activeTiers.push({ id: 1, node: node1, target: t1, label: node1.getAttribute('data-label') || '' });
    if (t2 > 0 && node2) this.activeTiers.push({ id: 2, node: node2, target: t2, label: node2.getAttribute('data-label') || '' });
    if (t3 > 0 && node3) this.activeTiers.push({ id: 3, node: node3, target: t3, label: node3.getAttribute('data-label') || '' });

    // 4. Calculate node positions
    this.activeTiers.forEach((tier, index) => {
      const position = ((index + 1) / this.activeTiers.length) * 75;
      this.style.setProperty(`--node-${tier.id}-pos`, `${position}%`);
    });

    // 5. Read initial total price or grab it from an attribute
    const initialPrice = parseFloat(this.getAttribute('cart-total') ?? '') || 0;
    this.updateShippingBar(initialPrice);

    document.addEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate.bind(this));
}

  /**
   * @param {Number} cartTotal - The click event
  */

  updateShippingBar(cartTotal) {
    if (!this.barFill || !this.textMsg) return;
    
    let percentage = 0;
    let message = "";

    /**
     * @param {Number} amount - The click event
     */
    
    const formatCurrency = (amount) => {
      return `$${(amount / 100).toFixed(2)}`;
    };

    // Reset nodes
    this.activeTiers.forEach(t => t.node.classList.remove('achieved'));

    const nextTierIndex = this.activeTiers.findIndex(t => t.target > cartTotal);
    if (nextTierIndex === -1) {
      percentage = 100;
      message = "🌟 Amazing! You've unlocked all rewards!";
      this.activeTiers.forEach(t => t.node.classList.add('achieved'));
    } else {
      const currentTier = this.activeTiers[nextTierIndex];
      const prevTarget = nextTierIndex === 0 ? 0 : this.activeTiers[nextTierIndex - 1]?.target;
      
      for (let i = 0; i < nextTierIndex; i++) {
        this.activeTiers[i]?.node.classList.add('achieved');
      }

      const sectionSize = 100 / this.activeTiers.length;
      if(prevTarget === undefined || !currentTier) return;
      const segmentProgress = (cartTotal - prevTarget) / (currentTier.target - prevTarget);
      percentage = ((nextTierIndex * sectionSize) + (segmentProgress * sectionSize)) * .75;

      const remainder = currentTier.target - cartTotal;
      // ONJ-CUSTOM-START · onj-tier-labels · 2026-10
      // Label is typed in theme settings and already includes "Free ..."; escape it for innerHTML.
      const safeLabel = String(currentTier.label).replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
      message = `Only <span style="color: var(--shipping-bar-fill-color);">${formatCurrency(remainder)}</span> to get ${safeLabel}`;
      // ONJ-CUSTOM-END
    }

    if (!(this.barFill instanceof HTMLElement)) return;
    this.barFill.style.width = `${Math.min(Math.max(percentage, 0), 100)}%`;
    this.textMsg.innerHTML = message;
  }

  async handleCartUpdate() {
    return fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then((response) => {
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
      return response.json();
    }).then((cart) => {
        this.updateShippingBar(cart.total_price)
    });
  }

  /**
   * Handles the cart update event.
   * @param {CartLinesUpdateEvent} event - The cart update event.
   */
    onCartUpdate(event) {
        event.promise
        ?.then((res) => {
            const { cart } = res;
            this.updateShippingBar(Number(cart?.cost.totalAmount.amount) * 100);
        })
        .catch((error) => {
            if (error?.name !== 'AbortError') console.warn('[free-shipping-bar] Event promise rejected:', error);
        });
    };
}

// Define the custom element tag
customElements.define('free-shipping-bar', FreeShippingBar);